import { useCallback, useEffect, useMemo, useState } from 'react'
import type { APIDataSet, APIOrganization } from '@visorpro/client'
import { MaterialReactTable, MRT_Cell, MRT_ExpandButton, MRT_Row, MRT_TableInstance, type MRT_ColumnDef } from 'material-react-table'
import {
    Box,
    Button,
    Dialog,
    Stack,
    TextField,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Chip,
    Tooltip,
    IconButton,
    alpha,
    useTheme,
} from '@mui/material'
import { Info, Folder, CorporateFare } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { formatDate } from '../util/format'
import { useAdminMaterialReactTable } from './table/mui-table'
import { DictionaryArray } from '../types'
import { useMultiValueColumnDef } from './table/column-definition'
import { SearchableSelectOption } from './searchable-select'
import _ from 'lodash'
import { brand } from '../theme/primitives'


export interface DataSetsTableProps {
    dataSets: DictionaryArray<APIDataSet>
    organizations?: DictionaryArray<APIOrganization>
    isLoading?: boolean
    showProgressBars?: boolean
    rowCount?: number
    isPrimaryBackgroundColor?: boolean
    isTreeTable?: boolean
    createDataSet?: (name: string) => Promise<void>
    updateDataSet?: (
        dataSetId: string,
        body: { name?: string, parent_data_set_id?: string | null },
    ) => Promise<void>
    deleteDataSet?: (dataSetId: string) => Promise<void>
    setDataSetOrganizations?: (id: string, additions: string[], deletions: string[]) => Promise<void>
}

export const DataSetsTable = ({
    dataSets,
    organizations,
    rowCount,
    isLoading,
    showProgressBars,
    isPrimaryBackgroundColor,
    isTreeTable,
    createDataSet,
    updateDataSet,
    deleteDataSet,
    setDataSetOrganizations,
}: DataSetsTableProps) => {
    const [isCreatingDataSet, setIsCreatingDataSet] = useState(false)
    const [isDeletingDataSets, setIsDeletingDataSets] = useState(false)
    const [dataSetsDeletedSnackbarOpen, setDataSetsDeletedSnackbarOpen] =
        useState(false)
    const [topLevelDataSets, setTopLevelDataSets] = useState<APIDataSet[]>([])
    const [nestedDataSetsByParentId, setNestedDataSetsByParentId] = useState<
        Record<string, APIDataSet[]>
    >({})

    const renderOrganizationChip = (organization: APIOrganization, index: number, isVirtual?: boolean) => {
        const chip = (
            <Chip
                label={organization.name}
                size="small"
                title={organization.name}
                clickable={!isVirtual}
                icon={<CorporateFare />}
                sx={{ opacity: isVirtual ? 0.5 : 1, borderStyle: isVirtual ? 'dashed' : 'solid', }}
            />
        )

        if (isVirtual) {
            return <Box key={index}>{chip}</Box>
        }

        return (
            <Link
                className="text-link"
                to={`/organizations/${organization.id}/data-sets`}
                key={index}>
                {chip}
            </Link>
        )
    }

    const getOrganizationOptions = (
        organizations: APIOrganization[],
    ): SearchableSelectOption[] => {
        return organizations.map((organization) => ({
            label: organization.name,
            value: organization.id,
        }))
    }

    const organizationColumnDefs = useMultiValueColumnDef<APIDataSet, APIOrganization>({
        fieldName: 'Organizations',
        optionsData: organizations,
        renderChip: renderOrganizationChip,
        getOptions: getOrganizationOptions,
        onEdit: setDataSetOrganizations,
    })

    useEffect(() => {
        const topLevel: APIDataSet[] = []
        const nestedDataSetsByParentId: Record<string, APIDataSet[]> = {}

        if (isTreeTable) {
            dataSets.forEach((dataSet) => {
                if (dataSet.parent_data_set_id) {
                    if (!nestedDataSetsByParentId[dataSet.parent_data_set_id]) {
                        nestedDataSetsByParentId[dataSet.parent_data_set_id] = []
                    }
                    nestedDataSetsByParentId[dataSet.parent_data_set_id].push(dataSet)
                } else {
                    topLevel.push(dataSet)
                }
            })
        } else {
            topLevel.push(...dataSets)
        }

        table.setColumnVisibility((updaterOrValue) => {
            const currentVisibility =
                updaterOrValue instanceof Function
                    ? updaterOrValue()
                    : updaterOrValue

            return {
                ...currentVisibility,
                'mrt-row-expand': !!isTreeTable,
                'organizations': organizations !== undefined,
            }
        })

        setTopLevelDataSets(topLevel)
        setNestedDataSetsByParentId(nestedDataSetsByParentId)
    }, [dataSets, isTreeTable, organizations])

    const DataSetFolderTreeButtons = ({ row, table }: { row: MRT_Row<APIDataSet>, table: MRT_TableInstance<APIDataSet> }) => {
        const enabledExpanding = table.options.enableExpanding
        const canExpand = row.getCanExpand()
        const isExpanded = row.getIsExpanded()

        return (
            <>
                {Array.from({ length: row.depth }).map((_, index) => (
                    <Box
                        key={index}
                        sx={{
                            position: 'absolute',
                            width: '1.4px',
                            top: 0,
                            height: '100%',
                            backgroundColor: alpha(brand[200], 0.25),
                            left: `${22 + index * 24}px`,
                            borderRadius: '999px',
                        }}
                    />
                ))}
                {canExpand && isExpanded && (
                    <Box
                        sx={{
                            position: 'absolute',
                            zIndex: -1,
                            width: '1.4px',
                            bottom: 0,
                            height: '50%',
                            backgroundColor: alpha(brand[200], 0.25),
                            left: `${22 + row.depth * 24}px`,
                            borderRadius: '999px',
                        }}
                    />
                )}
                {enabledExpanding && <MRT_ExpandButton row={row} table={table} />}
            </>
        )
    }

    const columns = useMemo<MRT_ColumnDef<APIDataSet>[]>(
        () => [
            {
                accessorKey: 'id',
                id: 'id',
                header: 'ID',
                enableClickToCopy: true,
                size: 400,
            },
            {
                accessorKey: 'name',
                header: 'Name',
                size: 400,
                Cell: ({ row, cell, table }) => {
                    const cellRenderValue: any = cell.renderValue()

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', }}>
                            <DataSetFolderTreeButtons row={row} table={table} />
                            {cellRenderValue}
                        </Box>
                    )
                },
                ...(updateDataSet && {
                    enableEditing: true,
                    Edit: ({ cell, row, table }) => {
                        return <>
                            <DataSetFolderTreeButtons row={row} table={table} />
                            <TextField
                                defaultValue={cell.getValue<string>()}
                                autoFocus
                                type="text"
                                label="Name"
                                required
                                fullWidth
                                variant='standard'
                                onBlur={(event) => {
                                    const name = event.target.value
                                    const id = row.id
                                    const currentName = cell.getValue()
                                    if (name !== currentName && name.trim() !== '') {
                                        void updateDataSet(id, { name })
                                    }
                                    table.setEditingCell(null)
                                }}
                            />
                        </>
                    }
                }),
            },
            {
                accessorKey: 'organizations',
                header: 'Organizations',
                size: 400,
                visibleInShowHideMenu: organizations !== undefined,
                ...organizationColumnDefs,
                Cell: ({ row }) => {
                    const organizations = row.original.organizations ?? []
                    const parentRows = row.getParentRows().sort((a, b) => b.depth - a.depth)
                    const parentOrganizations = _.flatten(parentRows.map(parentRow => parentRow.original.organizations ?? []))
                    const inheritOrganizations = _.differenceBy(parentOrganizations, organizations, 'id')
                    const uniqueInheritOrganizations = _.uniqBy(inheritOrganizations, 'id')

                    return <Box display="flex" alignItems="left" gap={0.6} flexWrap="wrap">
                        {organizations.map((organization, index) => renderOrganizationChip(organization, index))}
                        {uniqueInheritOrganizations.map((organization, index) => renderOrganizationChip(organization, organizations.length + index, true))}
                    </Box>
                },
            },
            {
                accessorKey: 'documents_count',
                header: 'Documents',
            },
            {
                accessorFn: (doc) => doc.updated_at,
                header: 'Updated At',
                id: 'updated_at',
                size: 300,
                Cell: ({ row }) => formatDate(row.getValue('updated_at')),
                filterVariant: 'datetime-range',
            },
            {
                accessorFn: (doc) => doc.created_at,
                header: 'Created At',
                id: 'created_at',
                size: 300,
                Cell: ({ row }) => formatDate(row.getValue('created_at')),
                filterVariant: 'datetime-range',
            },
        ],
        [organizationColumnDefs],
    )

    const table = useAdminMaterialReactTable(
        {
            columns,
            data: topLevelDataSets,
            rowCount,
            enableRowSelection: true,
            enableSorting: true,
            enableRowDragging: updateDataSet !== undefined,
            initialState: {
                columnPinning: { left: ['name'] },
                sorting: [{ id: 'created_at', desc: true }],
            },
            defaultColumn: {
                enableColumnFilter: true,
                enableSorting: true,
            },
            displayColumnDefOptions: {
                'mrt-row-select': {
                    size: 45,
                },
                'mrt-row-expand': {
                    size: 0,
                    minSize: 0,
                    maxSize: 0,
                    enableResizing: false,
                    muiTableHeadCellProps: { sx: { display: 'none' } },
                    muiTableBodyCellProps: { sx: { display: 'none' } },
                },
            },
            getRowId: (row) => row.id,
            state: {
                isLoading,
                showProgressBars,
            },
            renderTopToolbarCustomActions: ({ table }) => {
                const onCreateDataSet = () =>
                    setIsCreatingDataSet(true)
                const onDeleteDataSets = () =>
                    setIsDeletingDataSets(true)
                const selectedRows = table.getSelectedRowModel().flatRows

                return (
                    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                        {createDataSet && (
                            <Button onClick={onCreateDataSet}>
                                Create data set
                            </Button>
                        )}
                        {deleteDataSet && (
                            <Button
                                onClick={onDeleteDataSets}
                                disabled={selectedRows.length === 0}>
                                {selectedRows.length > 1
                                    ? `Delete ${selectedRows.length} data sets`
                                    : 'Delete data set'}
                            </Button>
                        )}
                    </Box>
                )
            },
            enableRowActions: true,
            renderRowActions: ({ row }) => {
                const id = row.id

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '8px' }}>
                        <Link
                            className="text-link"
                            to={`/data-sets/${id}`}>
                            <Tooltip title="Details">
                                <span>
                                    <IconButton
                                        size="small"
                                        title="Open details">
                                        <Info fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Link>
                    </Box>
                )
            },
            isPrimaryBackgroundColor,
            enableExpanding: !!isTreeTable,
            filterFromLeafRows: true,
            getSubRows: (originalRow) => {
                const dataSetId = originalRow.id
                const children = nestedDataSetsByParentId[dataSetId]
                return children
            },
            enableRowOrdering: updateDataSet !== undefined,
            muiRowDragHandleProps: ({ table, row }) => ({
                onDragStart: () => {
                    row.toggleExpanded(false)
                },
                onDragEnd: () => {
                    const { draggingRow, hoveredRow } = table.getState();

                    if (draggingRow && hoveredRow) {
                        const draggingRowId = draggingRow.id;
                        const hoveredRowId = hoveredRow.id;

                        if (draggingRowId !== hoveredRowId) {
                            const draggingRowOldParentId = draggingRow.original.parent_data_set_id;
                            const hoveredRowParentId = hoveredRow.original?.parent_data_set_id ?? null;

                            const isHoveredOnParent = draggingRowOldParentId === hoveredRowId
                            const newParentId = isHoveredOnParent ? hoveredRowParentId : hoveredRowId;

                            updateDataSet?.(draggingRowId, {
                                parent_data_set_id: newParentId,
                            });

                        }
                    }
                },
                onDragCapture: () => {
                    const { draggingRow, hoveredRow } = table.getState();
                    if (draggingRow && hoveredRow) {
                        const draggingRowId = draggingRow.id;
                        const hoveredRowId = hoveredRow.id;
                        table.setShowAlertBanner(draggingRowId !== hoveredRowId);
                    }
                },
                onDragEndCapture: () => {
                    table.setShowAlertBanner(false);
                },
            }),
            muiToolbarAlertBannerProps: ({ table }) => {
                const { draggingRow, hoveredRow } = table.getState();

                if (draggingRow && hoveredRow) {
                    const draggingRowName = draggingRow.original.name
                    const hoveredRowName = hoveredRow.original?.name

                    const hoveredRowId = hoveredRow.id;
                    const draggingRowOldParentId = draggingRow.original.parent_data_set_id;
                    const isHoveredOnParent = draggingRowOldParentId === hoveredRowId

                    const hoveredRowParentId = hoveredRow.original?.parent_data_set_id;
                    const newParentName = hoveredRowParentId && dataSets.get(hoveredRowParentId)?.name

                    return {
                        children: (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, }}>
                                Moving <Chip label={draggingRowName} icon={<Folder />} size="small" />{' '}
                                {isHoveredOnParent ? 'outside of' : 'under'}{' '}
                                <Chip label={hoveredRowName} icon={<Folder />} size="small" />
                                {isHoveredOnParent && (
                                    <>
                                        {"(and into "}
                                        {newParentName ? (
                                            <Chip label={newParentName} icon={<Folder />} size="small" />
                                        ) : (
                                            'top level'
                                        )}
                                        {")"}
                                    </>
                                )}
                            </Box>
                        ),
                    }
                }

                return {
                    children: null,
                }
            },
            muiExpandButtonProps: ({ row, table }) => {
                const theme = useTheme();
                const {
                    options: {
                        icons: { ExpandMoreIcon },
                        positionExpandColumn,
                        renderDetailPanel,
                    },
                } = table;

                const canExpand = row.getCanExpand();
                const isExpanded = row.getIsExpanded();
                const detailPanel = !!renderDetailPanel?.({ row, table });

                return {
                    children: <ExpandMoreIcon
                        style={{
                            transform: `rotate(${!canExpand && !renderDetailPanel
                                ? positionExpandColumn === 'last' ||
                                    theme.direction === 'rtl'
                                    ? 270
                                    : 0
                                : isExpanded
                                    ? 0
                                    : -90
                                }deg)`,
                            transition: 'transform 150ms',
                        }}
                    />,
                    sx: {
                        opacity: !canExpand && !detailPanel ? 0 : 1,
                        mr: 2,
                        [theme.direction === 'rtl' || positionExpandColumn === 'last'
                            ? 'mr'
                            : 'ml']: `${row.depth * 24}px`,
                    },
                }
            },
        },
        'data-sets-table',
    )


    const onDeleteDataSets = async (dataSetIds: string[]) => {
        await Promise.allSettled(
            dataSetIds.map((dataSetId) =>
                deleteDataSet?.(dataSetId),
            ),
        )
        table.resetRowSelection()
        setDataSetsDeletedSnackbarOpen(true)
        setIsDeletingDataSets(false)
    }

    const onDataSetsDeletedSnackbarClose = () =>
        setDataSetsDeletedSnackbarOpen(false)
    return (
        <>
            <MaterialReactTable table={table} />
            <CreateDataSetDialog
                open={isCreatingDataSet}
                setOpen={setIsCreatingDataSet}
                createDataSet={createDataSet}
            />
            <DeleteDataSetDialog
                open={isDeletingDataSets}
                setOpen={setIsDeletingDataSets}
                selectedDataSets={table
                    .getSelectedRowModel()
                    .flatRows.map(
                        (row) => row.original as APIDataSet,
                    )}
                deleteDataSets={onDeleteDataSets}
            />
            <Snackbar
                open={dataSetsDeletedSnackbarOpen}
                autoHideDuration={2000}
                onClose={onDataSetsDeletedSnackbarClose}
                message="Data sets deleted successfully"
            />
        </>
    )
}

interface CreateDataSetDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    createDataSet?: (name: string) => void
}

const CreateDataSetDialog = ({
    open,
    setOpen,
    createDataSet,
}: CreateDataSetDialogProps) => {
    const [name, setName] = useState<string>('')

    const onCreate = () => {
        createDataSet?.(name)
        onClose()
    }
    const onClose = () => setOpen(false)

    useEffect(() => {
        if (open) {
            setName('')
        }
    }, [open])

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Create data set</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <Stack spacing={1} sx={{ py: 1 }}>
                        <TextField
                            required
                            id="create-data-set-name"
                            label="Name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Stack>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={onCreate}
                    disabled={name === ''}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DeleteDataSetDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    selectedDataSets: APIDataSet[]
    deleteDataSets: (dataSetIds: string[]) => void
}

const DeleteDataSetDialog = ({
    open,
    setOpen,
    selectedDataSets,
    deleteDataSets,
}: DeleteDataSetDialogProps) => {
    const onDelete = () => {
        deleteDataSets(selectedDataSets.map((model) => model.id))
        onClose()
    }
    const onClose = () => setOpen(false)

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Delete data sets</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <div>
                        Are you sure you want to delete the following{' '}
                        {selectedDataSets.length} data sets?
                    </div>
                    <Box
                        sx={{
                            marginTop: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}>
                        {selectedDataSets.map((dataSet, index) => (
                            <Chip
                                label={dataSet.name}
                                title={dataSet.name}
                                key={index}
                            />
                        ))}
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={onDelete}>
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const toOrdinal = (n: number): string => {
    const s = ["th", "st", "nd", "rd"]
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
}
